import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  getFirestore,
  query,
  orderBy,
} from "firebase/firestore";
import { useState, useEffect, useCallback, useMemo } from "react";
import { app } from "../firebase";

/**
 * Custom hook for performing CRUD operations on a 'recipes' subcollection
 * within a specific 'list' document in Firestore.
 * @param {string} listId The ID of the parent list document.
 */
export const useRecipesCRUD = (listId) => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const db = useMemo(() => getFirestore(app), [app]);

  const recipesCollectionRef = useMemo(() => {
    if (!listId) {
      return null;
    }
    return query(
      collection(db, "lists", listId, "recipes"),
      orderBy("name", "asc")
    );
  }, [db, listId]);

  const fetchRecipes = useCallback(async () => {
    if (!recipesCollectionRef) {
      setRecipes([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(recipesCollectionRef);
      const fetchedRecords = querySnapshot.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));
      setRecipes(fetchedRecords);
    } catch (err) {
      console.error("Error fetching recipes:", err);
      setError(err.message || "Failed to fetch recipes.");
    } finally {
      setLoading(false);
    }
  }, [recipesCollectionRef]);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes, refetchTrigger]);

  const addRecipe = useCallback(
    async (newRecipe) => {
      if (!recipesCollectionRef) {
        setError("List ID is not defined, cannot add recipe.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const plainCollectionRef = collection(db, "lists", listId, "recipes");
        await addDoc(plainCollectionRef, newRecipe);
        setRefetchTrigger((prev) => prev + 1);
      } catch (err) {
        console.error("Error adding recipe:", err);
        setError(err.message || "Failed to add recipe.");
      } finally {
        setLoading(false);
      }
    },
    [db, listId]
  );

  const updateRecord = useCallback(
    async (id, updatedData) => {
      if (!listId) {
        setError("List ID is not defined, cannot update recipe.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const recipeDocRef = doc(db, "lists", listId, "recipes", id);
        await updateDoc(recipeDocRef, updatedData);
        setRefetchTrigger((prev) => prev + 1);
      } catch (err) {
        console.error("Error updating recipe:", err);
        setError(err.message || "Failed to update recipe.");
      } finally {
        setLoading(false);
      }
    },
    [db, listId]
  );

  const deleteRecord = useCallback(
    async (id) => {
      if (!listId) {
        setError("List ID is not defined, cannot delete recipe.");
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const recipeDocRef = doc(db, "lists", listId, "recipes", id);
        await deleteDoc(recipeDocRef);
        setRefetchTrigger((prev) => prev + 1);
      } catch (err) {
        console.error("Error deleting recipe:", err);
        setError(err.message || "Failed to delete recipe.");
      } finally {
        setLoading(false);
      }
    },
    [db, listId]
  );

  return {
    recipes,
    loading,
    error,
    addRecipe,
    updateRecord,
    deleteRecord,
    refetchRecords: () => setRefetchTrigger((prev) => prev + 1),
  };
};
